import React, { useState } from "react";
import {
  Typography,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Box,
  Card,
  CardHeader,
  CardContent,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";

import { Edit, Delete } from "@mui/icons-material";

import { Link as RouterLink, useNavigate } from "react-router-dom";
import { styled } from "@mui/material/styles";
import EngineeringIcon from "@mui/icons-material/Engineering";
import { createTheme } from "@mui/material/styles";
import { postTechnician } from "../API/technicianAPI";
import BaseFormWrapper from "../../components/shared/BaseFormWrapper";

const theme = createTheme({
  palette: {
    primary: {
      main: "#1e5d45", // Custom primary color
    },
  },
});

const StyledHr = styled("hr")(({ theme }) => ({
  border: 0,
  borderTop: `1px solid ${theme.palette.divider}`,
  // margin: theme.spacing(4, 0),
  opacity: 0.8,
}));

const competencyLabels = [
  { label: "General Pest Control", key: "general_pest_control" },
  { label: "Termite Control", key: "termite_control" },
  { label: "QPMS", key: "qpms" },
  { label: "Disinfection", key: "disinfection" },
  { label: "Basic Safety", key: "basic_safety" },
  { label: "Driving (2 wheels)", key: "driving_2_wheels" },
  { label: "Driving (4 wheels)", key: "driving_4_wheels" },
];

const professionalLabels = [
  {
    category: "GPC",
    items: [
      { label: "Rodent", key: "rodent" },
      { label: "Baiting", key: "baiting" },
      { label: "Cockroach", key: "cockroach" },
      { label: "Fly Control", key: "fly_control" },
      { label: "Ant Control", key: "ant_control" },
      { label: "Larviciding", key: "larviciding" },
    ],
  },
  {
    category: "Termite",
    items: [
      { label: "Compre", key: "compre" },
      { label: "AG", key: "ag" },
      { label: "IG", key: "ig" },
      { label: "IC", key: "ic" },
      { label: "Spraying", key: "spraying" },
      { label: "Soil Poisoning", key: "soil_poisoning" },
    ],
  },
];

function AddTechnician() {
  const navigate = useNavigate();

  const [technicianParameters, setTechnicianParameters] = useState({
    firstname: "",
    lastname: "",
    team: "",
    team_leader: "no",
    rest_day: [],
    off_service: [],
    competency_level: {},
    professional_level: professionalLabels.reduce((acc, category) => {
      acc[category.category.toLowerCase()] = {};
      category.items.forEach((item) => {
        acc[category.category.toLowerCase()][item.key] = "N/A";
      });
      return acc;
    }, {}),
  });
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const handleTechnicianChange = (field, value) => {
    setTechnicianParameters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleChange = (event) => {
    const { value, checked } = event.target;
    handleTechnicianChange(
      "rest_day",
      checked
        ? [...technicianParameters.rest_day, value]
        : technicianParameters.rest_day.filter((item) => item !== value)
    );
  };

  const handleCompetencyLevel = (field, value) => {
    setTechnicianParameters((prev) => ({
      ...prev,
      competency_level: {
        ...prev.competency_level, // Spread the existing fields in competency_level
        [field]: value, // Update or add the specific field
      },
    }));
  };

  const handleProfessionalLevel = (category, field, value) => {
    setTechnicianParameters((prev) => ({
      ...prev,
      professional_level: {
        ...prev.professional_level, // Preserve other categories in professional_level
        [category]: {
          ...prev.professional_level[category], // Preserve other fields in the specific category
          [field]: value, // Update the specific field
        },
      },
    }));
  };

  const competencyLevels = [
    "N/A",
    "1",
    "2",
    "3",
    "4",
    "5",
    "6",
    "7",
    "8",
    "9",
    "10",
  ];
  const professionalLevels = [
    "Team Leader",
    "High",
    "Mid",
    "Basic",
    "For observation",
  ];
  const options = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];

  const [offService, setOffService] = useState({
    date: "",
    reason: "on-leave",
  });
  const [isOffServiceEdit, setIsOffServiceEdit] = useState(false);
  const [originalDate, setOriginalDate] = useState(null);

  const handleOffServiceChange = (event) => {
    const { name, value } = event.target;
    setOffService((prev) => ({ ...prev, [name]: value }));
  };

  const handleOffServiceAddOrUpdate = () => {
    setTechnicianParameters((prev) => {
      if (isOffServiceEdit) {
        // Update the existing record
        return {
          ...prev,
          off_service: prev.off_service.map((record) =>
            record.date === originalDate
              ? { date: offService.date, reason: offService.reason }
              : record
          ),
        };
      }

      // Add a new record
      return {
        ...prev,
        off_service: [
          ...prev.off_service,
          { date: offService.date, reason: offService.reason },
        ],
      };
    });

    // Reset the form
    setOffService({ date: "", reason: "on-leave" });
    setIsOffServiceEdit(false);
    setOriginalDate(null);
  };

  const handleOffServiceEdit = (record) => {
    setOffService({ date: record.date, reason: record.reason });
    setIsOffServiceEdit(true);
    setOriginalDate(record.date);
  };

  const handleOffServiceDelete = (date) => {
    setTechnicianParameters((prev) => ({
      ...prev,
      off_service: prev.off_service.filter((record) => record.date !== date),
    }));
  };

  async function handleSubmit(e) {
    e.preventDefault();
    setIsLoading(true);

    const response = await postTechnician(technicianParameters);
    setIsLoading(false);

    if (response) {
      setSuccessMessage("Technician added successfully!");

      // Reset form fields
      setTechnicianParameters({
        firstname: "",
        lastname: "",
        team: "",
        team_leader: "no",
        rest_day: [],
        off_service: [],
        competency_level: {},
        professional_level: professionalLabels.reduce((acc, category) => {
          acc[category.category.toLowerCase()] = {};
          category.items.forEach((item) => {
            acc[category.category.toLowerCase()][item.key] = "N/A";
          });
          return acc;
        }, {}),
      });
    } else {
      setErrorMessage(
        "An error occurred while adding the Technician. Please try again later."
      );
    }
  }

  return (

    <BaseFormWrapper
      titleIcon={<EngineeringIcon />}
      title="Operations"
      subTitle="Manage Technicians"
      subTitleRoute="/manage-technicians"
      formTitle="Add Technician"
      successMessage={successMessage}
      setSuccessMessage={setSuccessMessage}
      errorMessage={errorMessage}
      setErrorMessage={setErrorMessage}
      isLoading={isLoading}
    >
      <form
        onSubmit={handleSubmit}
        className="technician-form"
        autoComplete="off"
      >
        <Grid container>
          <Grid
            item
            xs={12}
            md={6}
            sx={{ display: "flex", flexDirection: "column" }}
          >
            <Card
              variant="outlined"
              sx={{ marginRight: "5px" }}
            >
              <CardHeader
                title={
                  <Typography
                    variant="h6"
                  >
                    Basic Information
                  </Typography>
                }
              />
              <CardContent
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Grid container spacing={2} sx={{ flex: 1 }}>
                  <Grid item xs={6}>
                    <TextField
                      name="firstname"
                      label="First Name"
                      value={technicianParameters.firstname}
                      fullWidth
                      onChange={(e) =>
                        handleTechnicianChange(
                          "firstname",
                          e.target.value
                        )
                      }
                      size="small"
                      InputProps={{ sx: { fontSize: "0.875rem" } }}
                      InputLabelProps={{
                        sx: { fontSize: "0.875rem" },
                      }}
                      required
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      name="lastname"
                      label="Last Name"
                      value={technicianParameters.lastname}
                      fullWidth
                      onChange={(e) =>
                        handleTechnicianChange(
                          "lastname",
                          e.target.value
                        )
                      }
                      size="small"
                      InputProps={{ sx: { fontSize: "0.875rem" } }}
                      InputLabelProps={{
                        sx: { fontSize: "0.875rem" },
                      }}
                      required
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel
                        id="team-label"
                        sx={{ fontSize: "0.875rem" }}
                      >
                        Team
                      </InputLabel>
                      <Select
                        labelId="team-label"
                        name="type"
                        value={technicianParameters.team}
                        label="Team"
                        sx={{ fontSize: "0.875rem" }}
                        onChange={(e) =>
                          handleTechnicianChange("team", e.target.value)
                        }
                      >
                        <MenuItem value="Delta">Delta</MenuItem>
                        <MenuItem value="Sigma">Sigma</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel
                        id="team-label"
                        sx={{ fontSize: "0.875rem" }}
                      >
                        Team Leader
                      </InputLabel>
                      <Select
                        labelId="team-label"
                        name="type"
                        value={technicianParameters.team_leader}
                        label="Team Leader"
                        sx={{ fontSize: "0.875rem" }}
                        onChange={(e) =>
                          handleTechnicianChange("team_leader", e.target.value)
                        }
                      >
                        <MenuItem value="yes">Yes</MenuItem>
                        <MenuItem value="no">No</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12}>
                    <StyledHr />
                  </Grid>

                  <Grid item xs={12}>
                    <CardHeader
                      title={
                        <Typography
                          variant="h6"
                        >
                          Rest Days
                        </Typography>
                      }
                    />
                    <FormGroup>
                      <Grid container flexWrap>
                        {options.map((option) => (
                          <Grid item key={option}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={technicianParameters.rest_day.includes(
                                    option
                                  )}
                                  onChange={handleChange}
                                  value={option}
                                />
                              }
                              label={option}
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </FormGroup>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card
              variant="outlined"
            >
              <CardHeader
                title={
                  <Typography
                    variant="h6"
                  >
                    Competency Level
                  </Typography>
                }
              />
              <CardContent>
                {competencyLabels.map(({ label, key }) => (
                  <Grid
                    container
                    spacing={2}
                    alignItems="center"
                    sx={{ marginBottom: 3 }}
                    key={key}
                  >
                    <Grid item xs={3}>
                      <Typography>
                        {label}
                      </Typography>
                    </Grid>
                    <Grid item xs={9}>
                      <Grid container spacing={3}>
                        <Grid item xs={12}>
                          <Grid container spacing={2}>
                            <Grid item xs={3}>
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "center",
                                }}
                              >
                                {competencyLevels.includes("N/A") && (
                                  <Button
                                    variant={
                                      technicianParameters
                                        .competency_level[key] === "N/A"
                                        ? "contained"
                                        : "outlined"
                                    }
                                    onClick={() =>
                                      handleCompetencyLevel(key, "N/A")
                                    }
                                    color="primary"
                                    sx={{
                                      minWidth: "40px",
                                      height: "40px",
                                      borderRadius: "8px",
                                    }}
                                  >
                                    N/A
                                  </Button>
                                )}
                              </Box>
                            </Grid>
                            <Grid item xs={9}>
                              <Box
                                sx={{
                                  display: "flex",
                                  gap: 1,
                                  flexWrap: "wrap",
                                }}
                              >
                                {competencyLevels
                                  .filter((value) => value !== "N/A")
                                  .map((value) => (
                                    <Button
                                      key={value}
                                      variant={
                                        technicianParameters
                                          .competency_level[key] ===
                                          value
                                          ? "contained"
                                          : "outlined"
                                      }
                                      onClick={() =>
                                        handleCompetencyLevel(
                                          key,
                                          value
                                        )
                                      }
                                      color="primary"
                                      sx={{
                                        minWidth: "40px",
                                        height: "40px",
                                        borderRadius: "8px",
                                      }}
                                    >
                                      {value}
                                    </Button>
                                  ))}
                              </Box>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                ))}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Grid container>
              {professionalLabels.map((category) => (
                <Grid item xs={12} sm={6} key={category.category}>
                  <Card
                    variant="outlined"
                    sx={{ marginRight: "5px" }}
                  >
                    <CardHeader
                      title={
                        <Typography
                          variant="h6"
                        >
                          Professional Level ({category.category})
                        </Typography>
                      }
                    />
                    <CardContent>
                      {category.items.map((item) => (
                        <Grid
                          container
                          spacing={2}
                          alignItems="center"
                          sx={{ marginBottom: 3 }}
                          key={item.key}
                        >
                          <Grid item xs={3}>
                            <Typography
                              sx={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                flexWrap: "nowrap",
                              }}
                            >
                              {item.label}
                            </Typography>
                          </Grid>
                          <Grid item xs={9}>
                            <Box
                              sx={{
                                display: "flex",
                                gap: 1,
                                flexWrap: "wrap",
                              }}
                            >
                              {professionalLevels.map((value) => (
                                <Button
                                  key={value}
                                  variant={
                                    technicianParameters
                                      .professional_level[
                                      category.category.toLowerCase()
                                    ][item.key] === value
                                      ? "contained"
                                      : "outlined"
                                  }
                                  color="primary"
                                  onClick={() =>
                                    handleProfessionalLevel(
                                      category.category.toLowerCase(),
                                      item.key,
                                      value
                                    )
                                  }
                                  sx={{
                                    minWidth: {
                                      xs: "30px",
                                      sm: "35px",
                                      md: "40px",
                                    },
                                    height: {
                                      xs: "30px",
                                      sm: "35px",
                                      md: "40px",
                                    },
                                    borderRadius: "8px",
                                    fontSize: {
                                      xs: "0.75rem",
                                      sm: "0.75rem",
                                      md: "0.75rem",
                                    },
                                  }}
                                >
                                  {value}
                                </Button>
                              ))}
                            </Box>
                          </Grid>
                        </Grid>
                      ))}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Card
                  variant="outlined"
                >
                  <CardHeader
                    title={
                      <Typography
                        variant="h6"
                      >
                        Off Service
                      </Typography>
                    }
                  />
                  <CardContent>
                    <FormGroup>
                      {/* Input Row */}
                      <Grid container spacing={2} alignItems="center">
                        <Grid item>
                          <TextField
                            name="date"
                            type="date"
                            value={offService.date}
                            onChange={handleOffServiceChange}
                            sx={{ width: "200px" }}
                          />
                        </Grid>
                        <Grid item>
                          <Select
                            name="reason"
                            value={offService.reason}
                            onChange={handleOffServiceChange}
                            sx={{ width: "150px" }}
                          >
                            <MenuItem value="on-leave">
                              On-Leave
                            </MenuItem>
                            <MenuItem value="suspended">
                              Suspended
                            </MenuItem>
                          </Select>
                        </Grid>
                        <Grid item>
                          <Button
                            variant="contained"
                            onClick={handleOffServiceAddOrUpdate}
                            sx={{ height: "40px" }}
                          >
                            {isOffServiceEdit ? "Update" : "Add"}
                          </Button>
                        </Grid>
                      </Grid>

                      {/* Table Row */}
                      <TableContainer sx={{ marginTop: "2rem" }}>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>Date</TableCell>
                              <TableCell>Reason</TableCell>
                              <TableCell>Action</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {technicianParameters.off_service
                              .sort(
                                (a, b) =>
                                  new Date(b.date) - new Date(a.date)
                              ) // Sort by date, newest first
                              .map((record, index) => (
                                <TableRow key={index}>
                                  <TableCell>{record.date}</TableCell>
                                  <TableCell>{record.reason}</TableCell>
                                  <TableCell>
                                    <IconButton
                                      onClick={() =>
                                        handleOffServiceEdit(record)
                                      }
                                    >
                                      <Edit />
                                    </IconButton>
                                    <IconButton
                                      onClick={() =>
                                        handleOffServiceDelete(
                                          record.date
                                        )
                                      }
                                    >
                                      <Delete />
                                    </IconButton>
                                  </TableCell>
                                </TableRow>
                              ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </FormGroup>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Grid>

          <Grid
            item
            xs={12}
            display="flex"
            justifyContent="flex-end"
            alignItems="center"
            mt={2}
            mb={2}
            mr={2}
          >
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => navigate("/manage-technicians")}
              sx={{
                fontSize: "0.875rem",
                color: "#FF4C4C",
                borderColor: "#FF4C4C",
              }}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              sx={{
                marginLeft: "10px",
                fontSize: "0.875rem",
              }}
              disabled={isLoading}
            >
              {isLoading ? "Submitting..." : "Submit"}
            </Button>
          </Grid>
        </Grid>
      </form>


    </BaseFormWrapper>
  );
}

export default AddTechnician;
